// ================================================================
// 管理員問題價格管理頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-27
// 功能：管理諮詢問題的價格（單一/批次調整）
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  Toolbar,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  SelectAll,
  Deselect,
  Edit,
  PriceChange,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getAdminQuestions,
  updateQuestionPrice,
  batchUpdatePrice,
  getPriceStats,
  Question,
  CategoryStats,
  PriceCategoryStats,
  PriceOverallStats,
} from '../../api/admin';

// 類別名稱映射
const categoryNames: { [key: string]: string } = {
  FULL: '完整分析',
  PAIR: '配對分析',
  MAIN: '自我分析',
  COMMON: '常見問題',
  SIMPLE: '優惠咨詢',
};

// 類別顏色
const categoryColors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' } = {
  FULL: 'primary',
  PAIR: 'secondary',
  MAIN: 'success',
  COMMON: 'warning',
  SIMPLE: 'info',
};

// 調整模式名稱
const modeNames: { [key: string]: string } = {
  fixed: '設定固定價格',
  percent: '百分比調整',
  amount: '金額調整',
};

const AdminPricePage: React.FC = () => {
  // 狀態
  const [questions, setQuestions] = useState<Question[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [priceStats, setPriceStats] = useState<PriceCategoryStats[]>([]);
  const [overallStats, setOverallStats] = useState<PriceOverallStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  // 單一編輯對話框
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editPrice, setEditPrice] = useState<string>('');
  const [editIsFree, setEditIsFree] = useState(false);

  // 批次調整對話框
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchMode, setBatchMode] = useState<'fixed' | 'percent' | 'amount'>('fixed');
  const [batchValue, setBatchValue] = useState<string>('');

  // 載入問題列表
  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAdminQuestions(categoryFilter);
      if (response.success) {
        setQuestions(response.questions || []);
        setCategoryStats(response.category_stats || []);
      } else {
        setError(response.message || '無法載入問題列表');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter]);

  // 載入價格統計
  const loadPriceStats = useCallback(async () => {
    try {
      const response = await getPriceStats();
      if (response.success) {
        setPriceStats(response.category_stats || []);
        setOverallStats(response.overall_stats || null);
      }
    } catch (err: any) {
      console.error('Failed to load price stats:', err);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
    loadPriceStats();
  }, [loadQuestions, loadPriceStats]);

  // 開啟單一編輯對話框
  const handleOpenEdit = (question: Question) => {
    setEditingQuestion(question);
    setEditPrice(question.price.toString());
    setEditIsFree(question.is_free === 1);
    setEditDialogOpen(true);
  };

  // 關閉單一編輯對話框
  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingQuestion(null);
    setEditPrice('');
    setEditIsFree(false);
  };

  // 儲存單一價格
  const handleSavePrice = async () => {
    if (!editingQuestion) return;

    const price = parseFloat(editPrice);
    if (isNaN(price) || price < 0) {
      setError('請輸入有效的價格');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const response = await updateQuestionPrice(
        editingQuestion.question_id,
        editIsFree ? 0 : price,
        editIsFree
      );
      if (response.success) {
        setSuccess(response.message);
        handleCloseEdit();
        loadQuestions();
        loadPriceStats();
      } else {
        setError(response.message || '更新失敗');
      }
    } catch (err: any) {
      setError(err.message || '更新失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 開啟批次調整對話框
  const handleOpenBatch = () => {
    setBatchMode('fixed');
    setBatchValue('');
    setBatchDialogOpen(true);
  };

  // 關閉批次調整對話框
  const handleCloseBatch = () => {
    setBatchDialogOpen(false);
    setBatchMode('fixed');
    setBatchValue('');
  };

  // 執行批次調整
  const handleBatchUpdate = async () => {
    const value = parseFloat(batchValue);
    if (isNaN(value)) {
      setError('請輸入有效的數值');
      return;
    }

    if (batchMode === 'fixed' && value < 0) {
      setError('固定價格不能為負數');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const response = await batchUpdatePrice(
        batchMode,
        value,
        selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
        selectedIds.size === 0 && categoryFilter !== 'all' ? categoryFilter : undefined
      );
      if (response.success) {
        setSuccess(response.message);
        handleCloseBatch();
        setSelectedIds(new Set());
        loadQuestions();
        loadPriceStats();
      } else {
        setError(response.message || '批次更新失敗');
      }
    } catch (err: any) {
      setError(err.message || '批次更新失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 全選/取消全選
  const handleSelectAll = () => {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.question_id)));
    }
  };

  // 選擇單一項目
  const handleSelectOne = (questionId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedIds(newSelected);
  };

  // 格式化價格
  const formatPrice = (price: number, isFree: number) => {
    if (isFree === 1) return '免費';
    return `NT$ ${price.toLocaleString()}`;
  };

  if (isLoading && questions.length === 0) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              問題價格管理
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理諮詢問題的價格，支援單一編輯與批次調整
            </Typography>
          </Box>
          <Tooltip title="重新載入">
            <IconButton onClick={() => { loadQuestions(); loadPriceStats(); }} disabled={isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 訊息提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* 價格統計卡片 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  問題總數
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {overallStats?.total_count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  免費問題
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {overallStats?.free_count || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  平均價格
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  NT$ {Math.round(overallStats?.avg_price || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  已選取
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  {selectedIds.size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 類別價格統計 */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {priceStats.map(stat => (
            <Chip
              key={stat.category_id}
              label={`${categoryNames[stat.category_id] || stat.category_id}: NT$ ${Math.round(stat.avg_price)} (${stat.min_price}-${stat.max_price})`}
              color={categoryColors[stat.category_id] || 'default'}
              variant={categoryFilter === stat.category_id ? 'filled' : 'outlined'}
              onClick={() => setCategoryFilter(stat.category_id === categoryFilter ? 'all' : stat.category_id)}
            />
          ))}
        </Box>

        {/* 工具列 */}
        <Paper sx={{ mb: 2 }}>
          <Toolbar sx={{ gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>類別篩選</InputLabel>
              <Select
                value={categoryFilter}
                label="類別篩選"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">全部類別</MenuItem>
                <MenuItem value="FULL">完整分析</MenuItem>
                <MenuItem value="PAIR">配對分析</MenuItem>
                <MenuItem value="MAIN">自我分析</MenuItem>
                <MenuItem value="COMMON">常見問題</MenuItem>
                <MenuItem value="SIMPLE">優惠咨詢</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={selectedIds.size === questions.length ? <Deselect /> : <SelectAll />}
              onClick={handleSelectAll}
            >
              {selectedIds.size === questions.length ? '取消全選' : '全選'}
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<PriceChange />}
              onClick={handleOpenBatch}
              disabled={isProcessing}
            >
              批次調整價格
            </Button>
          </Toolbar>
        </Paper>

        {/* 問題列表 */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedIds.size === questions.length && questions.length > 0}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < questions.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>問題 ID</TableCell>
                <TableCell>類別</TableCell>
                <TableCell>問題內容</TableCell>
                <TableCell align="right">目前價格</TableCell>
                <TableCell align="center">狀態</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((question) => (
                <TableRow
                  key={question.question_id}
                  sx={{
                    opacity: question.is_active ? 1 : 0.6,
                    bgcolor: selectedIds.has(question.question_id) ? 'action.selected' : 'inherit',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(question.question_id)}
                      onChange={() => handleSelectOne(question.question_id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {question.question_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={categoryNames[question.category_id] || question.category_id}
                      size="small"
                      color={categoryColors[question.category_id] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap>
                      {question.question_text}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {question.is_free === 1 ? (
                      <Chip label="免費" size="small" color="success" />
                    ) : (
                      <Typography variant="body2" fontWeight="bold">
                        NT$ {question.price.toLocaleString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={question.is_active ? '啟用' : '停用'}
                      size="small"
                      color={question.is_active ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="編輯價格">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(question)}
                        disabled={isProcessing}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {questions.length === 0 && !isLoading && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">沒有符合條件的問題</Typography>
          </Box>
        )}

        {/* 單一編輯對話框 */}
        <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle>編輯問題價格</DialogTitle>
          <DialogContent>
            {editingQuestion && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  問題 ID: {editingQuestion.question_id}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {editingQuestion.question_text}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editIsFree}
                      onChange={(e) => setEditIsFree(e.target.checked)}
                    />
                  }
                  label="設為免費"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="價格"
                  type="number"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  disabled={editIsFree}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">NT$</InputAdornment>,
                  }}
                  inputProps={{ min: 0 }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEdit}>取消</Button>
            <Button
              variant="contained"
              onClick={handleSavePrice}
              disabled={isProcessing}
            >
              儲存
            </Button>
          </DialogActions>
        </Dialog>

        {/* 批次調整對話框 */}
        <Dialog open={batchDialogOpen} onClose={handleCloseBatch} maxWidth="sm" fullWidth>
          <DialogTitle>批次調整價格</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                {selectedIds.size > 0
                  ? `將調整 ${selectedIds.size} 個選取的問題`
                  : categoryFilter !== 'all'
                  ? `將調整「${categoryNames[categoryFilter]}」類別的所有問題`
                  : '請先選取問題或篩選類別'}
              </Alert>

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">調整模式</FormLabel>
                <RadioGroup
                  value={batchMode}
                  onChange={(e) => setBatchMode(e.target.value as 'fixed' | 'percent' | 'amount')}
                >
                  <FormControlLabel
                    value="fixed"
                    control={<Radio />}
                    label="設定固定價格 - 所有選取問題設為相同價格"
                  />
                  <FormControlLabel
                    value="percent"
                    control={<Radio />}
                    label="百分比調整 - 依目前價格調整（正數漲價、負數降價）"
                  />
                  <FormControlLabel
                    value="amount"
                    control={<Radio />}
                    label="金額調整 - 增加或減少固定金額"
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                fullWidth
                label={
                  batchMode === 'fixed'
                    ? '新價格'
                    : batchMode === 'percent'
                    ? '調整百分比'
                    : '調整金額'
                }
                type="number"
                value={batchValue}
                onChange={(e) => setBatchValue(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {batchMode === 'fixed'
                        ? 'NT$'
                        : batchMode === 'percent'
                        ? '%'
                        : 'NT$'}
                    </InputAdornment>
                  ),
                  endAdornment: batchMode === 'percent' && (
                    <InputAdornment position="end">
                      {parseFloat(batchValue) >= 0 ? (
                        <TrendingUp color="success" />
                      ) : (
                        <TrendingDown color="error" />
                      )}
                    </InputAdornment>
                  ),
                }}
                helperText={
                  batchMode === 'fixed'
                    ? '所有選取問題將設為此價格'
                    : batchMode === 'percent'
                    ? '例如: 10 表示漲價 10%，-20 表示降價 20%'
                    : '例如: 50 表示每題加 NT$50，-30 表示每題減 NT$30'
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBatch}>取消</Button>
            <Button
              variant="contained"
              onClick={handleBatchUpdate}
              disabled={
                isProcessing ||
                !batchValue ||
                (selectedIds.size === 0 && categoryFilter === 'all')
              }
            >
              確認調整
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default AdminPricePage;
